import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import { Icon } from 'leaflet';
import { getMercados } from '../services/firebase';
import Loading from './common/Loading';
import logo from '../assets/ma2.png';
import styles from '../styles/map.module.css';
import 'leaflet/dist/leaflet.css';

// ===== ICONOS =====
import { 
  FaArrowLeft, 
  FaStore, 
  FaClock, 
  FaMapMarkerAlt,
  FaChevronRight,
  FaInstagram,
  FaWhatsapp
} from 'react-icons/fa';

// ===== COMPONENTE PARA CENTRAR EL MAPA =====
const MapCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 13);
    }
  }, [center, map]);
  return null;
};

// ===== ICONO PERSONALIZADO PARA MERCADOS =====
const mercadoIcon = new Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36">
      <circle cx="12" cy="12" r="10" fill="#43115E" stroke="white" stroke-width="2"/>
      <path d="M4 7h16l-2 12H6L4 7z" fill="white" opacity="0.9"/>
      <path d="M8 3v4M16 3v4" stroke="white" stroke-width="2" stroke-linecap="round"/>
      <line x1="9" y1="11" x2="15" y2="11" stroke="#43115E" stroke-width="2"/>
      <line x1="9" y1="14" x2="15" y2="14" stroke="#43115E" stroke-width="2"/>
    </svg>
  `),
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

// ===== ICONO PARA UBICACIÓN DEL USUARIO =====
const userIcon = new Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#2563EB" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const Map = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mercados, setMercados] = useState([]);
  const [mercadosFiltrados, setMercadosFiltrados] = useState([]);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [obteniendoUbicacion, setObteniendoUbicacion] = useState(true);
  const [radio, setRadio] = useState(5);

  // Calcular distancia entre dos puntos (fórmula de Haversine)
  const calcularDistancia = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  // Filtrar mercados por radio
  const filtrarMercadosPorRadio = useCallback((location, mercadosList, radioValue) => {
    if (!location || !mercadosList) return [];
    
    if (radioValue === 0) {
      return mercadosList;
    }
    
    return mercadosList.filter(m => {
      const distancia = calcularDistancia(
        location.lat, location.lng,
        m.geolocalizacion.lat, m.geolocalizacion.lng
      );
      return distancia <= radioValue;
    });
  }, [calcularDistancia]);

  // Cambiar radio
  const cambiarRadio = (nuevoRadio) => {
    setRadio(nuevoRadio);
    if (userLocation && mercados.length > 0) {
      const filtrados = filtrarMercadosPorRadio(userLocation, mercados, nuevoRadio);
      setMercadosFiltrados(filtrados);
    }
  };

  // Cargar mercados y obtener ubicación
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError(null);

        const mercadosData = await getMercados();
        const mercadosConCoords = mercadosData.filter(m => 
          m.geolocalizacion?.lat && m.geolocalizacion?.lng
        );
        setMercados(mercadosConCoords);

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const location = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              setUserLocation(location);
              setObteniendoUbicacion(false);
              const filtrados = filtrarMercadosPorRadio(location, mercadosConCoords, radio);
              setMercadosFiltrados(filtrados);
            },
            (error) => {
              console.error('Error obteniendo ubicación:', error);
              setObteniendoUbicacion(false);
              const defaultLocation = { lat: 19.7025, lng: -101.1925 };
              setUserLocation(defaultLocation);
              const filtrados = filtrarMercadosPorRadio(defaultLocation, mercadosConCoords, radio);
              setMercadosFiltrados(filtrados);
            },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        } else {
          setObteniendoUbicacion(false);
          const defaultLocation = { lat: 19.7025, lng: -101.1925 };
          setUserLocation(defaultLocation);
          const filtrados = filtrarMercadosPorRadio(defaultLocation, mercadosConCoords, radio);
          setMercadosFiltrados(filtrados);
        }

      } catch (error) {
        console.error('Error cargando datos:', error);
        setError('Error al cargar los datos. Intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, []);

  // Ordenar mercados por distancia
  const mercadosOrdenados = userLocation 
    ? [...mercadosFiltrados].sort((a, b) => {
        const distA = calcularDistancia(
          userLocation.lat, userLocation.lng,
          a.geolocalizacion.lat, a.geolocalizacion.lng
        );
        const distB = calcularDistancia(
          userLocation.lat, userLocation.lng,
          b.geolocalizacion.lat, b.geolocalizacion.lng
        );
        return distA - distB;
      })
    : mercadosFiltrados;

  const irAMercado = (id) => {
    navigate(`/mercado/${id}`);
  };

  if (loading || obteniendoUbicacion) {
    return (
      <>
        <header className={styles.mapHeader}>
          <div className={styles.mapHeaderContainer}>
            <Link to="/" className={styles.mapLogo}>
              <img src={logo} alt="Mercado Abierto" className={styles.mapLogoImg} />
            </Link>
          </div>
        </header>
        <Loading message={obteniendoUbicacion ? 'Obteniendo ubicación...' : 'Cargando mercados...'} />
        <footer className={styles.mapFooter}>
          <div className={styles.mapFooterContent}>
            <p>© {new Date().getFullYear()} mercadoabierto.online</p>
          </div>
        </footer>
      </>
    );
  }

  if (error) {
    return (
      <>
        <header className={styles.homeHeader}>
        <div className={styles.homeHeaderContainer}>
          <Link to="/" className={styles.homeLogo}>
            <img
              src={logo}
              alt="Mercado Abierto"
              className={styles.homeLogoImg}
            />
          </Link>
        </div>
      </header>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={() => window.location.reload()} className={styles.reloadButton}>
            🔄 Intentar de nuevo
          </button>
        </div>
        <footer className={styles.mapFooter}>
          <div className={styles.mapFooterContent}>
            <p>© {new Date().getFullYear()} mercadoabierto.online</p>
          </div>
        </footer>
      </>
    );
  }

  const mapCenter = userLocation || { lat: 19.7025, lng: -101.1925 };

  return (
    <div className={styles.mapContainer}>

       <header className={styles.homeHeader}>
        <div className={styles.homeHeaderContainer}>
          <Link to="/" className={styles.homeLogo}>
            <img
              src={logo}
              alt="Mercado Abierto"
              className={styles.homeLogoImg}
            />
          </Link>
        </div>
      </header>

      <main className={styles.mapMainContent}>
        <div className={styles.mapWrapper}>
          
          {/* Botón volver */}
          <button onClick={() => navigate('/')} className={styles.backButton}>
            <FaArrowLeft size={16} />
            Volver al inicio
          </button>

          {/* Control de radio */}
          <div className={styles.radioControl}>
            <span className={styles.radioLabel}>📍 Mostrar mercados en un radio de:</span>
            <div className={styles.radioButtons}>
              <button 
                className={`${styles.radioBtn} ${radio === 2 ? styles.active : ''}`}
                onClick={() => cambiarRadio(2)}
              >
                2 km
              </button>
              <button 
                className={`${styles.radioBtn} ${radio === 5 ? styles.active : ''}`}
                onClick={() => cambiarRadio(5)}
              >
                5 km
              </button>
              <button 
                className={`${styles.radioBtn} ${radio === 10 ? styles.active : ''}`}
                onClick={() => cambiarRadio(10)}
              >
                10 km
              </button>
              <button 
                className={`${styles.radioBtn} ${radio === 0 ? styles.active : ''}`}
                onClick={() => cambiarRadio(0)}
              >
                Todos
              </button>
            </div>
          </div>

          {/* Mapa */}
          <div className={styles.mapContainerLeaflet}>
            <MapContainer
              center={mapCenter}
              zoom={13}
              className={styles.leafletMap}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapCenter center={mapCenter} />

              {userLocation && radio > 0 && (
                <Circle
                  center={[userLocation.lat, userLocation.lng]}
                  radius={radio * 1000}
                  pathOptions={{
                    color: '#43115E',
                    fillColor: '#43115E',
                    fillOpacity: 0.08,
                    weight: 2,
                    dashArray: '8 6'
                  }}
                />
              )}

              {userLocation && (
                <Marker
                  position={[userLocation.lat, userLocation.lng]}
                  icon={userIcon}
                >
                  <Popup>
                    <div className={styles.userPopup}>
                      <strong>📍 Tu ubicación</strong>
                    </div>
                  </Popup>
                </Marker>
              )}

              {mercadosOrdenados.map((mercado) => (
                <Marker
                  key={mercado.id}
                  position={[mercado.geolocalizacion.lat, mercado.geolocalizacion.lng]}
                  icon={mercadoIcon}
                >
                  <Popup>
                    <div className={styles.marketPopup}>
                      {mercado.portada && (
                        <img 
                          src={mercado.portada} 
                          alt={mercado.nombre} 
                          className={styles.popupImage} 
                        />
                      )}
                      <div className={styles.popupInfo}>
                        <h4>{mercado.nombre}</h4>
                        <p className={styles.popupLocation}>
                          <FaMapMarkerAlt size={12} />
                          {mercado.direccion?.ciudad || 'Morelia'}, {mercado.direccion?.estado || 'Michoacán'}
                        </p>
                        <div className={styles.popupHorario}>
                          <FaClock size={12} />
                          <span>Horario: {mercado.horarios ? 'Ver horarios' : 'No especificado'}</span>
                        </div>
                        <button 
                          className={styles.popupButton}
                          onClick={() => irAMercado(mercado.id)}
                        >
                          Ver mercado →
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* Lista de mercados */}
          <div className={styles.mercadosLista}>
            <h3 className={styles.listaTitle}>
              <FaStore size={20} />
              {mercadosOrdenados.length === 0 ? 'No hay mercados cercanos' : `${mercadosOrdenados.length} ${mercadosOrdenados.length === 1 ? 'mercado' : 'mercados'} en un radio de ${radio} km`}
            </h3>
            {mercadosOrdenados.length === 0 ? (
              <div className={styles.listaVacia}>
                <p>No hay mercados en un radio de {radio} km.</p>
                <button 
                  className={styles.verTodosBtn}
                  onClick={() => cambiarRadio(0)}
                >
                  Ver todos los mercados
                </button>
              </div>
            ) : (
              <div className={styles.listaItems}>
                {mercadosOrdenados.map((mercado) => {
                  const distancia = userLocation 
                    ? calcularDistancia(
                        userLocation.lat, userLocation.lng,
                        mercado.geolocalizacion.lat, mercado.geolocalizacion.lng
                      ).toFixed(1)
                    : null;
                  return (
                    <div 
                      key={mercado.id} 
                      className={styles.listaItem}
                      onClick={() => irAMercado(mercado.id)}
                    >
                      {mercado.portada && (
                        <img 
                          src={mercado.portada} 
                          alt={mercado.nombre} 
                          className={styles.listaItemImg} 
                        />
                      )}
                      <div className={styles.listaItemInfo}>
                        <h4>{mercado.nombre}</h4>
                        <p className={styles.listaItemUbicacion}>
                          {mercado.direccion?.ciudad || 'Morelia'}, {mercado.direccion?.estado || 'Michoacán'}
                        </p>
                        {distancia && (
                          <span className={styles.listaItemDistancia}>
                            📍 {distancia} km
                          </span>
                        )}
                      </div>
                      <FaChevronRight className={styles.listaItemArrow} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </main>

      {/* FOOTER */}
        <footer className={styles.mapFooter}>
        <div className={styles.mapFooterGrid}>
            <div>
            <img src={logo} alt="logo" className={styles.mapFooterLogo} />
            <p>Plataforma digital que conecta a los mercados municipales de México contigo.</p>
            </div>
            <div>
            <h4>Enlaces rápidos</h4>
            <p onClick={() => navigate('/')}>Inicio</p>
            <p onClick={() => navigate('/')}>Explorar mercados</p>
            <p onClick={() => navigate('/privacidad')}>Aviso de Privacidad</p>
            <p onClick={() => navigate('/terminos')}>Términos y Condiciones</p>
            </div>
            <div>
            <h4>Síguenos</h4>
            <div className={styles.socials}>
                <FaInstagram size={28} />
                <FaWhatsapp size={28} />
            </div>
            </div>
        </div>
        <div className={styles.mapFooterBottom}>
            © {new Date().getFullYear()} mercadoabierto.online - Todos los derechos reservados
        </div>
        </footer>

    </div>
  );
};

export default Map;